import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { Card, CardActionArea, CardContent, CardMedia, Chip, Stack, Theme, ThemeProvider, Typography, createTheme } from '@mui/material'
import LockOpenIcon from '@mui/icons-material/LockOpen'
import PublicIcon from '@mui/icons-material/Public'
import GroupRoundedIcon from '@mui/icons-material/GroupRounded'
import HourglassBottomRoundedIcon from '@mui/icons-material/HourglassBottomRounded'

import defaultNetworkCover from '@/assets/img/network-cover.png'

import { Network } from '@/core/models/network.model'
import dayjs from 'dayjs'

type NetworkCardProps = {
  isJoinRequestPending?: boolean
  network: {
    id: string
    coverPhoto?: string
    joined?: boolean
    memberCount: number
    name: string
    pendingMembers?: string[]
    pendingOrders: Array<{
      count: number
      type: 'consultation' | 'expertise'
    }>
    privacy: Network['privacy']
    isAdmin?: boolean
  }
  onSelect?: (networkId: string) => void
  selected?: boolean
}

export function NetworkCard(props: NetworkCardProps) {
  const { isJoinRequestPending, network, onSelect, selected = false } = props

  const { t } = useTranslation()

  return (
    <ThemeProvider
      theme={(theme: Theme) =>
        createTheme({
          ...theme,
          components: {
            ...theme.components,
            MuiSvgIcon: { defaultProps: { fontSize: 'small' }, styleOverrides: { root: { color: theme.palette.grey[500] } } },
          },
        })
      }
    >
      <Card
        sx={[
          { height: '100%', position: 'relative' },
          selected && {
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: 'primary.main',
            boxShadow: '0 2px 14px #3333',
          },
        ]}
      >
        <CardActionArea
          disableRipple={isJoinRequestPending}
          onClick={() => {
            if (isJoinRequestPending) return
            onSelect?.(network.id)
          }}
          sx={{ display: 'flex', flexDirection: 'column', alignItems: 'start', justifyContent: 'start', height: '100%' }}
        >
          <CardMedia component="img" height="140" image={network.coverPhoto ?? defaultNetworkCover} alt="network-cover" />
          <CardContent sx={{ position: 'relative', flexGrow: 1, width: '100%' }}>
            <Typography
              gutterBottom
              variant="h5"
              component={Link}
              to={`/networks/${network.id}`}
              sx={{
                display: 'inline-block',
                marginBottom: '8px !important',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              {network.name}
            </Typography>
            {network.isAdmin && (
              <Chip
                label={t('page.network.adminChip')}
                color="primary"
                size="small"
                sx={{ position: 'absolute', top:20, right: 20 }}
              />
            )}
            <Stack direction="row" alignItems="center" minHeight="30px" spacing={1}>
              {network.privacy === 'public' && <PublicIcon />}
              {network.privacy === 'private' && <LockOpenIcon />}
              <Typography variant="body2" color="text.secondary">
                {t(`page.network.privacy.${network.privacy}.primary`)}
              </Typography>
              {isJoinRequestPending && <Chip color="secondary" label="join request pending" size="small" />}
              {network.joined && <Chip color="success" label="joined" size="small" />}
            </Stack>
            <Stack direction="row" alignItems="center" minHeight="30px" spacing={1}>
              <GroupRoundedIcon />
              <Typography variant="body2" color="text.secondary">
                {t('card.network.memberCount', { count: network.memberCount })}
              </Typography>
            </Stack>
            {!!network.pendingMembers?.length && (
              <Stack direction="row" alignItems="center" minHeight="30px" spacing={1}>
                <HourglassBottomRoundedIcon />
                <Typography variant="body2" color="text.secondary">
                  {t('card.network.pendingMember', { count: network.pendingMembers.length })}
                </Typography>
              </Stack>
            )}
            {network.pendingOrders
              .filter(({ count }) => !!count)
              .map(({ count, type }, index) => (
                <Stack key={index} direction="row" alignItems="center" minHeight="30px" spacing={1}>
                  <HourglassBottomRoundedIcon />
                  <Typography variant="body2" color="text.secondary">
                    {t('card.network.pendingOrder', { count, context: type })}
                  </Typography>
                </Stack>
              ))}
          </CardContent>
        </CardActionArea>
      </Card>
    </ThemeProvider>
  )
}
